import ZonePicker from '../components/ZonePicker'
import { useSearchParams } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { Search, Filter, Ban, ChevronLeft, ChevronRight, Users as UsersIcon, UserCheck, UserPlus, ShieldCheck, Loader2, CheckCircle2, Trash2, X, Plus, MapPin } from 'lucide-react'
import toast from 'react-hot-toast'
import type { User, UserRole } from '../types'
import { fetchUsers, toggleUserStatus, changeUserRole, deleteUser, createUser, assignAdminZones } from '../services/adminService'
import { useAuthStore } from '../store/authStore'

const ROLE_STYLES: Record<UserRole, { bg: string; text: string; label: string }> = {
  RESIDENT: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Resident' },
  COLLECTOR: { bg: 'bg-green-100', text: 'text-green-700', label: 'Collector' },
  ADMIN: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Admin' },
  COMPANY_ADMIN: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Company' },
}

const ROLES: { value: UserRole | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All Roles' },
  { value: 'RESIDENT', label: 'Residents' },
  { value: 'COLLECTOR', label: 'Collectors' },
  { value: 'ADMIN', label: 'Admins' },
  { value: 'COMPANY_ADMIN', label: 'Company logins' },
]

const PAGE_SIZE = 8
const avatarColors = ['bg-blue-500', 'bg-green-600', 'bg-purple-500', 'bg-orange-500', 'bg-teal-500', 'bg-pink-500']

function getInitials(name: string) {
  return name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
}

export default function Users() {
  const currentUser = useAuthStore((s) => s.user)
  const isSuperAdmin = currentUser?.isSuperAdmin ?? false

  const [users, setUsers] = useState<User[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('search') ?? '')
  const [roleFilter, setRoleFilter] = useState<UserRole | 'ALL'>('ALL')
  const [page, setPage] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', email: '', phone: '', password: '', role: 'RESIDENT' as string, address: '' })
  const [createZones, setCreateZones] = useState<string[]>([])
  const [createSuperAdmin, setCreateSuperAdmin] = useState(false)
  const [creating, setCreating] = useState(false)

  // Zones modal state
  const [showZonesModal, setShowZonesModal] = useState(false)
  const [zonesTarget, setZonesTarget] = useState<{ id: string; name: string } | null>(null)
  const [editingZones, setEditingZones] = useState<string[]>([])
  const [zoneInput, setZoneInput] = useState('')
  const [savingZones, setSavingZones] = useState(false)

  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchUsers({
        search: search || undefined,
        role: roleFilter !== 'ALL' ? roleFilter : undefined,
        page,
        limit: PAGE_SIZE,
      })
      setUsers(data?.users ?? data ?? [])
      setTotal(data?.pagination?.total ?? data?.total ?? data?.length ?? 0)
    } catch {
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [search, roleFilter, page])

  useEffect(() => { loadUsers() }, [loadUsers])
  useEffect(() => {
    const fromUrl = searchParams.get('search')
    if (fromUrl != null && fromUrl !== search) { setSearch(fromUrl); setPage(1) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const totalPages = Math.ceil(total / PAGE_SIZE)

  const handleToggleStatus = async (userId: string, currentlyActive: boolean) => {
    if (!confirm(`${currentlyActive ? 'Suspend' : 'Activate'} this user?`)) return
    try {
      await toggleUserStatus(userId, !currentlyActive)
      toast.success(`User ${currentlyActive ? 'suspended' : 'activated'}`)
      loadUsers()
    } catch {
      toast.error('Failed to update user status')
    }
  }

  const handleRoleChange = async (userId: string, newRole: string, userName: string) => {
    if (!confirm(`Change ${userName}'s role to ${newRole}?`)) return
    try {
      await changeUserRole(userId, newRole)
      toast.success(`Role changed to ${newRole}`)
      loadUsers()
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to change role')
    }
  }

  const handleDelete = async (userId: string, userName: string) => {
    if (!confirm(`Delete user "${userName}"? This action may be irreversible.`)) return
    try {
      const result = await deleteUser(userId)
      toast.success(result?.deleted === 'soft' ? 'User deactivated and anonymized' : 'User permanently deleted')
      loadUsers()
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to delete user')
    }
  }

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error('Name, email, and password are required')
      return
    }
    setCreating(true)
    try {
      await createUser({
        name: createForm.name,
        email: createForm.email,
        phone: createForm.phone || undefined,
        password: createForm.password,
        role: createForm.role,
        address: createForm.address || undefined,
        ...(createForm.role === 'ADMIN' ? { zones: createZones, isSuperAdmin: createSuperAdmin } : {}),
      })
      toast.success(`${createForm.role} account created successfully`)
      setShowCreateModal(false)
      setCreateForm({ name: '', email: '', phone: '', password: '', role: 'RESIDENT', address: '' })
      setCreateZones([])
      setCreateSuperAdmin(false)
      loadUsers()
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to create user')
    } finally {
      setCreating(false)
    }
  }

  // Zone management handlers
  const handleOpenZones = (userId: string, userName: string, currentZones?: string[]) => {
    setZonesTarget({ id: userId, name: userName })
    setEditingZones(currentZones ?? [])
    setZoneInput('')
    setShowZonesModal(true)
  }

  const handleAddZone = () => {
    const zone = zoneInput.trim().toUpperCase()
    if (!zone) return
    if (editingZones.includes(zone)) {
      toast.error('Zone already added')
      return
    }
    setEditingZones((prev) => [...prev, zone])
    setZoneInput('')
  }

  const handleRemoveZone = (zone: string) => {
    setEditingZones((prev) => prev.filter((z) => z !== zone))
  }

  const handleSaveZones = async () => {
    if (!zonesTarget) return
    setSavingZones(true)
    try {
      await assignAdminZones(zonesTarget.id, editingZones)
      toast.success(`Zones updated for ${zonesTarget.name}`)
      setShowZonesModal(false)
      setZonesTarget(null)
      loadUsers()
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to update zones')
    } finally {
      setSavingZones(false)
    }
  }

  // Collect all known zones from existing admin users for suggestions
  const existingZones = Array.from(
    new Set(
      users
        .filter((u) => u.role === 'ADMIN' && (u as User & { assignedZones?: string[] }).assignedZones)
        .flatMap((u) => (u as User & { assignedZones?: string[] }).assignedZones as string[])
    )
  )

  const collectors = users.filter((u) => u.role === 'COLLECTOR').length

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: total, icon: UsersIcon, color: 'text-blue-500 bg-blue-50' },
          { label: 'Active', value: users.filter(u => u.isActive).length, icon: UserCheck, color: 'text-green-600 bg-green-50' },
          { label: 'Page Results', value: users.length, icon: UserPlus, color: 'text-orange-500 bg-orange-50' },
          { label: 'Collectors', value: collectors, icon: ShieldCheck, color: 'text-purple-600 bg-purple-50' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${color.split(' ')[1]} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${color.split(' ')[0]}`} />
            </div>
            <div>
              <p className="text-xs text-[#6B7280] font-medium">{label}</p>
              <p className="text-xl font-bold text-[#1A1A2E]">{value.toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-[#6B7280]" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="flex-1 bg-transparent text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#6B7280]" />
            <div className="flex bg-[#F8FAF9] rounded-xl border border-gray-200 overflow-hidden">
              {ROLES.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => { setRoleFilter(value); setPage(1) }}
                  className={`px-3 py-2 text-xs font-semibold transition-colors whitespace-nowrap ${
                    roleFilter === value
                      ? 'bg-primary-600 text-white'
                      : 'text-[#6B7280] hover:bg-gray-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 bg-primary-600 text-white px-4 py-2 rounded-xl text-xs font-semibold hover:bg-primary-700 transition-colors"
          >
            <Plus size={14} />
            Create User
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-[#1A1A2E]">
            {total} user{total !== 1 ? 's' : ''} found
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['User', 'Email', 'Phone', 'Role', 'Address', 'Joined', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map((user, idx) => {
                  // A role this table does not know must not take the page down.
                  const role = ROLE_STYLES[user.role] ?? { bg: 'bg-gray-100', text: 'text-gray-700', label: user.role }
                  const avatarColor = avatarColors[idx % avatarColors.length]
                  return (
                    <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full ${avatarColor} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                            {getInitials(user.name)}
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-[#1A1A2E] whitespace-nowrap">{user.name}</p>
                            <p className="text-[10px] text-[#6B7280]">{user.totalPickups ?? 0} pickups</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">{user.email}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">{user.phone}</td>
                      <td className="px-4 py-3">
                        {isSuperAdmin && user.role !== 'COMPANY_ADMIN' ? (
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.id, e.target.value, user.name)}
                            aria-label={`Change role for ${user.name}`}
                            className={`text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${role.bg} ${role.text}`}
                          >
                            <option value="RESIDENT">Resident</option>
                            <option value="COLLECTOR">Collector</option>
                            <option value="ADMIN">Admin</option>
                          </select>
                        ) : (
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${role.bg} ${role.text}`}>{role.label ?? user.role}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] max-w-[160px] truncate">{user.address ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${user.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                          {user.isActive ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {isSuperAdmin && user.role === 'ADMIN' && (
                            <button
                              type="button"
                              className="p-1.5 rounded-lg text-purple-500 hover:bg-purple-50 transition-colors"
                              title="Manage Zones"
                              onClick={() => handleOpenZones(user.id, user.name, (user as User & { assignedZones?: string[] }).assignedZones)}
                            >
                              <MapPin size={14} />
                            </button>
                          )}
                          <button
                            type="button"
                            className={`p-1.5 rounded-lg transition-colors ${
                              user.isActive
                                ? 'text-red-500 hover:bg-red-50'
                                : 'text-green-600 hover:bg-green-50'
                            }`}
                            title={user.isActive ? 'Suspend' : 'Activate'}
                            onClick={() => handleToggleStatus(user.id, user.isActive)}
                          >
                            {user.isActive ? <Ban size={14} /> : <CheckCircle2 size={14} />}
                          </button>
                          <button
                            type="button"
                            className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                            title="Delete User"
                            onClick={() => handleDelete(user.id, user.name)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-[#6B7280]">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-[#6B7280]">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                    p === page ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#1A1A2E]">Create New User</h3>
              <button type="button" onClick={() => setShowCreateModal(false)} className="p-1 rounded-lg hover:bg-gray-100 text-[#6B7280]">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Full Name *</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="John Doe"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Email Address *</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="john@example.com"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Phone</label>
                <input
                  type="tel"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="024XXXXXXX"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Password *</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="Minimum 8 characters"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1" id="create-user-role-label">Role *</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm(f => ({ ...f, role: e.target.value }))}
                  aria-labelledby="create-user-role-label"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary-500"
                >
                  <option value="RESIDENT">Resident</option>
                  <option value="COLLECTOR">Collector</option>
                  {isSuperAdmin && <option value="ADMIN">Admin</option>}
                </select>
                {createForm.role === 'ADMIN' && (
                  <div className="mt-3 space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
                    <label className="flex items-center gap-2 text-xs font-medium text-[#1A1A2E]">
                      <input type="checkbox" checked={createSuperAdmin} onChange={(e) => setCreateSuperAdmin(e.target.checked)} />
                      Super admin (sees every zone and can change pricing)
                    </label>
                    {!createSuperAdmin && (
                      <>
                        <p className="text-[11px] text-amber-800">
                          A regular admin sees only their zones. With none assigned the panel is empty for them.
                        </p>
                        <ZonePicker value={createZones} onChange={setCreateZones} />
                      </>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Address</label>
                <input
                  type="text"
                  value={createForm.address}
                  onChange={(e) => setCreateForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="Optional"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary-500"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-5">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-[#6B7280] bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateUser}
                disabled={creating}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating && <Loader2 size={14} className="animate-spin" />}
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Zones Assignment Modal */}
      {showZonesModal && zonesTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-bold text-[#1A1A2E]">Assign Zones</h3>
                <p className="text-xs text-[#6B7280] mt-0.5">Manage zones for {zonesTarget.name}</p>
              </div>
              <button type="button" onClick={() => setShowZonesModal(false)} className="p-1 rounded-lg hover:bg-gray-100 text-[#6B7280]" title="Close">
                <X size={18} />
              </button>
            </div>

            {/* Current zones chips */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-[#6B7280] mb-2">Assigned Zones</label>
              <div className="flex flex-wrap gap-2 min-h-[36px] p-2 bg-[#F8FAF9] rounded-xl border border-gray-200">
                {editingZones.length === 0 ? (
                  <span className="text-xs text-[#6B7280] italic py-0.5">No zones assigned</span>
                ) : (
                  editingZones.map((zone) => (
                    <span
                      key={zone}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full"
                    >
                      <MapPin size={10} />
                      {zone}
                      <button
                        type="button"
                        onClick={() => handleRemoveZone(zone)}
                        className="ml-0.5 hover:text-purple-900"
                        title={`Remove ${zone}`}
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>

            {/* Add zone input */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-[#6B7280] mb-1">Add Zone</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={zoneInput}
                  onChange={(e) => setZoneInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddZone() } }}
                  placeholder="Enter zone name (e.g., ZONE_A)"
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-primary-500"
                />
                <button
                  type="button"
                  onClick={handleAddZone}
                  disabled={!zoneInput.trim()}
                  className="px-3 py-2 bg-purple-600 text-white rounded-xl text-xs font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50"
                  title="Add zone"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            {/* Suggested zones from existing users */}
            {existingZones.length > 0 && (
              <div className="mb-4">
                <label className="block text-xs font-medium text-[#6B7280] mb-1.5">Existing Zones</label>
                <div className="flex flex-wrap gap-1.5">
                  {existingZones.filter((z) => !editingZones.includes(z)).map((zone) => (
                    <button
                      key={zone}
                      type="button"
                      onClick={() => setEditingZones((prev) => [...prev, zone])}
                      className="px-2.5 py-1 bg-gray-100 text-[#6B7280] text-xs font-medium rounded-full hover:bg-purple-50 hover:text-purple-700 transition-colors"
                      title={`Add ${zone}`}
                    >
                      + {zone}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 mt-5">
              <button
                type="button"
                onClick={() => setShowZonesModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-[#6B7280] bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveZones}
                disabled={savingZones}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-purple-600 rounded-xl hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingZones && <Loader2 size={14} className="animate-spin" />}
                Save Zones
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
